// Canonical workspace state for Weather Sicily Maps.
//
// This module deliberately has no DOM or MapLibre dependency. State is kept
// JSON-like so snapshots can be inspected, persisted and tested without a
// browser. Every committed snapshot is recursively frozen and inputs are
// cloned before entering the store.

export const TEMPORAL_MODES = Object.freeze({
    LIVE: 'live',
    HISTORY: 'history',
    FUTURE: 'future',
});

export const WORKSPACE_ACTIONS = Object.freeze({
    SET_TIME: 'workspace/set-time',
    SET_ACTIVE_SCENE: 'workspace/set-active-scene',
    SET_SECTION: 'workspace/set-section',
    PATCH_LAYER: 'workspace/patch-layer',
    PATCH_LAYERS: 'workspace/patch-layers',
    REMOVE_LAYER: 'workspace/remove-layer',
    SET_LOCATION: 'workspace/set-location',
    SET_CYCLE: 'workspace/set-cycle',
    SET_EXTENSION: 'workspace/set-extension',
});

const TEMPORAL_MODE_VALUES = new Set(Object.values(TEMPORAL_MODES));

function isPlainObject(value) {
    if (!value || typeof value !== 'object') return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

function cloneStateValue(value, seen = new WeakMap()) {
    if (value === null || value === undefined) return value;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    if (typeof value === 'bigint') return String(value);
    if (value instanceof Date) return value.toISOString();
    if (typeof value !== 'object') {
        throw new TypeError('Workspace state accepts only JSON-like values');
    }
    if (seen.has(value)) throw new TypeError('Workspace state cannot contain circular references');

    if (Array.isArray(value)) {
        const copy = [];
        seen.set(value, copy);
        value.forEach(item => copy.push(cloneStateValue(item, seen)));
        return copy;
    }
    if (!isPlainObject(value)) {
        throw new TypeError('Workspace state accepts only plain objects and arrays');
    }

    const copy = {};
    seen.set(value, copy);
    for (const [key, item] of Object.entries(value)) copy[key] = cloneStateValue(item, seen);
    return copy;
}

export function freezeWorkspaceSnapshot(value) {
    if (!value || typeof value !== 'object') return value;
    Object.values(value).forEach(freezeWorkspaceSnapshot);
    return Object.isFrozen(value) ? value : Object.freeze(value);
}

function copied(value) {
    return freezeWorkspaceSnapshot(cloneStateValue(value));
}

function finiteTime(value, label = 'targetMs') {
    const number = value instanceof Date ? value.getTime() : Number(value);
    if (!Number.isFinite(number)) throw new TypeError(`${label} must be a finite epoch timestamp`);
    return number;
}

function temporalMode(value) {
    if (!TEMPORAL_MODE_VALUES.has(value)) {
        throw new TypeError(`Unknown temporal mode: ${String(value)}`);
    }
    return value;
}

function nonEmptyKey(value, label) {
    const key = String(value ?? '').trim();
    if (!key) throw new TypeError(`${label} must be a non-empty string`);
    return key;
}

function initialState(initial) {
    const targetMs = initial.targetMs === undefined ? Date.now() : finiteTime(initial.targetMs);
    const location = isPlainObject(initial.location) ? initial.location : {};
    const cycle = isPlainObject(initial.cycle)
        ? initial.cycle
        : (initial.cycle == null ? {} : { id: String(initial.cycle) });

    return copied({
        revision: 0,
        targetMs,
        temporalMode: temporalMode(initial.temporalMode ?? TEMPORAL_MODES.LIVE),
        activeScene: String(initial.activeScene ?? 'live'),
        section: String(initial.section ?? 'overview'),
        layers: isPlainObject(initial.layers) ? initial.layers : {},
        location: {
            user: null,
            selected: null,
            ...location,
        },
        cycle: {
            id: null,
            generatedAtUtc: null,
            ...cycle,
        },
        extensions: isPlainObject(initial.extensions) ? initial.extensions : {},
    });
}

function commit(state, patch) {
    return copied({ ...state, ...patch, revision: state.revision + 1 });
}

export function reduceWorkspaceState(state, action) {
    if (!state || !action || typeof action.type !== 'string') {
        throw new TypeError('A workspace action with a string type is required');
    }

    switch (action.type) {
    case WORKSPACE_ACTIONS.SET_TIME: {
        const targetMs = finiteTime(action.targetMs);
        const mode = temporalMode(action.temporalMode);
        if (state.targetMs === targetMs && state.temporalMode === mode) return state;
        return commit(state, { targetMs, temporalMode: mode });
    }
    case WORKSPACE_ACTIONS.SET_ACTIVE_SCENE: {
        const activeScene = nonEmptyKey(action.activeScene, 'activeScene');
        if (state.activeScene === activeScene) return state;
        return commit(state, { activeScene });
    }
    case WORKSPACE_ACTIONS.SET_SECTION: {
        const section = nonEmptyKey(action.section, 'section');
        if (state.section === section) return state;
        return commit(state, { section });
    }
    case WORKSPACE_ACTIONS.PATCH_LAYER: {
        const id = nonEmptyKey(action.id, 'layer id');
        if (!isPlainObject(action.patch)) throw new TypeError('Layer patch must be a plain object');
        const patch = cloneStateValue(action.patch);
        return commit(state, {
            layers: {
                ...state.layers,
                [id]: { ...(state.layers[id] || {}), ...patch },
            },
        });
    }
    case WORKSPACE_ACTIONS.PATCH_LAYERS: {
        if (!isPlainObject(action.patches)) throw new TypeError('Layer patches must be a plain object');
        const layers = { ...state.layers };
        for (const [rawId, rawPatch] of Object.entries(action.patches)) {
            const id = nonEmptyKey(rawId, 'layer id');
            if (!isPlainObject(rawPatch)) throw new TypeError(`Layer patch for ${id} must be a plain object`);
            layers[id] = { ...(layers[id] || {}), ...cloneStateValue(rawPatch) };
        }
        return commit(state, { layers });
    }
    case WORKSPACE_ACTIONS.REMOVE_LAYER: {
        const id = nonEmptyKey(action.id, 'layer id');
        if (!(id in state.layers)) return state;
        const layers = { ...state.layers };
        delete layers[id];
        return commit(state, { layers });
    }
    case WORKSPACE_ACTIONS.SET_LOCATION: {
        if (!isPlainObject(action.patch)) throw new TypeError('Location patch must be a plain object');
        return commit(state, { location: { ...state.location, ...cloneStateValue(action.patch) } });
    }
    case WORKSPACE_ACTIONS.SET_CYCLE: {
        if (!isPlainObject(action.patch)) throw new TypeError('Cycle patch must be a plain object');
        return commit(state, { cycle: { ...state.cycle, ...cloneStateValue(action.patch) } });
    }
    case WORKSPACE_ACTIONS.SET_EXTENSION: {
        const namespace = nonEmptyKey(action.namespace, 'extension namespace');
        const value = cloneStateValue(action.value);
        const previous = state.extensions[namespace];
        const next = action.merge && isPlainObject(previous) && isPlainObject(value)
            ? { ...previous, ...value }
            : value;
        return commit(state, { extensions: { ...state.extensions, [namespace]: next } });
    }
    default:
        return state;
    }
}

export function createWorkspaceStore(initial = {}) {
    let state = initialState(initial);
    let destroyed = false;
    const listeners = new Set();

    const getState = () => state;

    const dispatch = rawAction => {
        if (destroyed) throw new Error('Workspace store has been destroyed');
        const action = copied(rawAction);
        const previous = state;
        const next = reduceWorkspaceState(previous, action);
        if (next === previous) return state;
        state = next;
        [...listeners].forEach(listener => listener(state, previous, action));
        return state;
    };

    const subscribe = (listener, options = {}) => {
        if (typeof listener !== 'function') throw new TypeError('Workspace listener must be a function');
        if (destroyed) throw new Error('Workspace store has been destroyed');
        listeners.add(listener);
        if (options.emitCurrent) listener(state, null, copied({ type: 'workspace/current' }));
        return () => listeners.delete(listener);
    };

    return Object.freeze({
        getState,
        dispatch,
        subscribe,
        setTime: (targetMs, mode) => dispatch({
            type: WORKSPACE_ACTIONS.SET_TIME,
            targetMs,
            temporalMode: mode,
        }),
        setActiveScene: activeScene => dispatch({ type: WORKSPACE_ACTIONS.SET_ACTIVE_SCENE, activeScene }),
        setSection: section => dispatch({ type: WORKSPACE_ACTIONS.SET_SECTION, section }),
        patchLayer: (id, patch) => dispatch({ type: WORKSPACE_ACTIONS.PATCH_LAYER, id, patch }),
        patchLayers: patches => dispatch({ type: WORKSPACE_ACTIONS.PATCH_LAYERS, patches }),
        removeLayer: id => dispatch({ type: WORKSPACE_ACTIONS.REMOVE_LAYER, id }),
        setLocation: patch => dispatch({ type: WORKSPACE_ACTIONS.SET_LOCATION, patch }),
        setCycle: patch => dispatch({ type: WORKSPACE_ACTIONS.SET_CYCLE, patch }),
        setExtension: (namespace, value, options = {}) => dispatch({
            type: WORKSPACE_ACTIONS.SET_EXTENSION,
            namespace,
            value,
            merge: !!options.merge,
        }),
        destroy: () => {
            destroyed = true;
            listeners.clear();
        },
    });
}
